import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import {Link, useLocation, useNavigate} from 'react-router';
import {
    Button,
    Card,
    Dropdown,
    OverlayTrigger,
    Tooltip,
} from '@goproblems/ui';
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion';
import {
    DailyProblemsButtons,
    GlobalBoardSettingsModal,
    PotentialDuplicates,
    ProblemFlagsModal,
    ProblemStarsModal,
    ChangeLevelModal,
} from '@components';
import {
    DailyProblemV2,
    NextProblemEntryEnum,
    OptimisticAttempts,
    ProblemGenreEnum,
    SingleProblem,
} from '@types';
import {
    getShortLevelName,
    MODAL_GLOBAL_BOARD_SETTINGS,
    MODAL_CHANGE_LEVEL,
    MODAL_PROBLEM_FLAGS,
    showModal,
    SPEED_SOLVE_FIRST_MOVE_TIME_LIMIT,
    SPEED_SOLVE_SUBSEQUENT_MOVE_TIME_LIMIT,
    useDispatch,
    useProblemFilterQueryParams,
    useSelector,
} from '@utils';
import {useTheme} from '@components/theme/useTheme';
import {t} from 'i18next';
import StarRating from '@components/ux/StarRating';
import ProblemCollection from '@components/ux/ProblemCollection';
import {ProblemCollectionHandle} from '@components/ux/ProblemCollection/functions/types';
import CollectionList from '@components/ux/ProblemCollection/CollectionList';
import dayjs from 'dayjs';
import {toast} from 'react-toastify';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
    faCalculator,
    faChevronDown,
    faEllipsis,
    faFlag as faFlagSolid,
    faGear,
    faSliders,
} from '@fortawesome/free-solid-svg-icons';
import {isMobile} from 'react-device-detect';
import {levelsApi} from '@goproblems/shared/api/levels';
import {LAST_KNOWN_LEVEL} from '@utils/const/level';
import RankText from '../common/RankText';
import {
    useFetchNextProblemPostMutation,
    useFetchProblemChangesCountQuery,
    useAdvanceSrsSessionMutation,
    useSkipProblemTrainingMutation,
    useStartSrsSessionMutation,
    useEndSrsSessionMutation,
} from '@api';
import {isNil, upperFirst} from 'lodash';
import {UserIdentity as User} from '@components/common/UserIdentity';
import {MobilePotdNextButton} from '@components/singleProblem/MobilePotdNextButton';
import SkipButton from '@components/singleProblem/SkipButton';
import NextBtn from '@components/singleProblem/NextBtn';
import SendTrainingAchievementRequest from '@components/training/SendTrainingAchievementRequest';
import {UserAttemptsContent} from '@components/singleProblem/header/UserAttemptsContent';
import {StarContent} from '@components/singleProblem/header/StarContent';
import {FlagButton} from '@components/singleProblem/header/FlagButton';
import StatusIcon from '@components/singleProblem/header/StatusIcon';
import {LevelCompletedModal} from '@components/singleProblem/LevelCompletedModal';
import {uiSlice} from '@reducers';
import {isFetchBaseQueryError} from '@utils/fetch';
import {buildNextProblemPath} from '@utils';

type ProblemHeaderProps = {
    problem: SingleProblem;
    dailyProblems?: DailyProblemV2[];
    nextButtonText?: string;
    hideStars?: boolean;
    hideGenre?: boolean;
    hideCheckMarks?: boolean;
    hideFlags?: boolean;
    hideNext?: boolean;
    compactLayout?: boolean;
};

export type ProblemHeaderRefType = {
    hideDetails: () => void;
    nextProblem: () => Promise<void>;
    showDetails: () => void;
    optimisticSolve: (solved: boolean, isSpeed: boolean) => void;
    checkLevelCompletion: (problemId: number) => Promise<void>;
    resetTimer: () => void;
    startTimer: () => void;
    stopTimer: () => void;
};

const AnimatedCollapse = ({
    children,
    expanded,
}: {
    children: ReactNode;
    expanded: boolean;
}) => {
    const shouldReduceMotion = useReducedMotion();

    return (
        <AnimatePresence initial={false}>
            {expanded && (
                <motion.div
                    initial={
                        shouldReduceMotion ? false : {height: 0, opacity: 0}
                    }
                    animate={{height: 'auto', opacity: 1}}
                    exit={{height: 0, opacity: 0}}
                    transition={{
                        duration: shouldReduceMotion ? 0 : 0.24,
                        ease: 'easeInOut',
                    }}
                    className="overflow-hidden">
                    {children}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const ProblemDetailItem = ({
    children,
    className = '',
    label,
}: {
    children: ReactNode;
    className?: string;
    label: ReactNode;
}) => (
    <div
        className={`grid min-w-0 grid-cols-[minmax(7.25rem,8.5rem)_minmax(0,1fr)] items-center gap-2 border-b border-black/10 px-3 py-2 text-sm sm:grid-cols-[minmax(7rem,14rem)_minmax(0,1fr)] sm:gap-3 sm:px-4 sm:py-2.5 sm:text-base dark:border-[var(--ui-border)] ${className}`}>
        <dt className="min-w-0 font-semibold text-neutral-700 dark:text-neutral-300">
            {label}
        </dt>
        <dd className="m-0 min-w-0 break-words">{children}</dd>
    </div>
);

export const ProblemHeader = forwardRef<
    ProblemHeaderRefType,
    ProblemHeaderProps
>(
    (
        {
            problem,
            dailyProblems,
            hideStars = false,
            hideGenre = false,
            hideCheckMarks = false,
            hideFlags = false,
            hideNext = false,
            compactLayout,
        },
        ref
    ) => {
        const mobileLayout = compactLayout ?? isMobile;
        const {user} = useSelector(state => state.auth);
        const canEditProblem = Boolean(user && problem.canBeEdited);
        const canChangeLevel = Boolean(
            (user?.isAdmin || user?.isEditor) &&
            problem.rank &&
            !problem.rank.mark
        );
        const {compressedPath} = useSelector(state => state.board);
        const trainingProblemIds = useSelector(
            state => state.ui.trainingProblemIds
        );
        const dispatch = useDispatch();
        const [canRate, setCanRate] = useState<boolean>(true);
        const [canNotRateReason, setCanNotRateReason] = useState<string | null>(
            null
        );
        const [showDetails, setShowDetails] = useState<boolean>(false);
        const suggestionHref = compressedPath
            ? `/suggestions/new?id=${problem.id}&path=${encodeURIComponent(compressedPath)}`
            : `/suggestions/new?id=${problem.id}`;

        const [optimisticAttempts, setOptimisticAttempts] =
            useState<OptimisticAttempts | null>(null);

        const theme = useTheme();

        const [timeLeft, setTimeLeft] = useState<number>(
            SPEED_SOLVE_FIRST_MOVE_TIME_LIMIT
        ); // Default to 5 seconds for first move
        const [isTimerRunning, setIsTimerRunning] = useState<boolean>(false);

        useEffect(() => {
            if (!isTimerRunning) return;

            const timer = setInterval(() => {
                setTimeLeft(prev => {
                    const newTime = Math.max(0, prev - 10);
                    if (newTime === 0) {
                        setIsTimerRunning(false);
                    }
                    return newTime;
                });
            }, 10);

            return () => clearInterval(timer);
        }, [isTimerRunning]);

        useEffect(() => {
            setOptimisticAttempts(null);
        }, [problem.currentUserAttempts]);

        useImperativeHandle(ref, () => ({
            hideDetails: () => setShowDetails(false),
            nextProblem: handleNext,
            checkLevelCompletion,
            showDetails: () => setShowDetails(true),
            optimisticSolve: (solved: boolean, isSpeed: boolean) => {
                const currentAttempts = problem.currentUserAttempts || {
                    tries: 0,
                    solved: 0,
                    speedSolved: 0,
                    speedPlay: 0,
                    hasSoftStopAttempts: false,
                };
                setOptimisticAttempts({
                    isAttempted: true,
                    isSolved: solved,
                    failed: 0,
                    speedPlay: 0,
                    tries: currentAttempts.tries + 1,
                    solved: solved
                        ? currentAttempts.solved + 1
                        : currentAttempts.solved,
                    speedSolved:
                        solved && isSpeed
                            ? currentAttempts.speedSolved + 1
                            : currentAttempts.speedSolved,
                    hasSoftStopAttempts: false,
                });
            },
            resetTimer,
            startTimer,
            stopTimer,
        }));

        const effectiveAttempts =
            optimisticAttempts || problem.currentUserAttempts;
        const collectionModalRef = useRef<ProblemCollectionHandle | null>(null);

        const navigate = useNavigate();
        const location = useLocation();

        const args = useProblemFilterQueryParams();
        const [completedLevel, setCompletedLevel] = useState<{
            level: number;
            speed: boolean;
            hasNextLevel: boolean;
        } | null>(null);
        const checkingCompletion = useRef(false);
        const completionContext = useRef<string | null>(
            `${user?.id}:${location.key}`
        );

        useEffect(() => {
            completionContext.current = `${user?.id}:${location.key}`;
            setCompletedLevel(null);
            return () => {
                completionContext.current = null;
            };
        }, [location.key, user?.id]);
        const isDailyProblemContext =
            new URLSearchParams(location.search).get('dailyProblem') === '1';
        const isDifficultyMode =
            args.name === NextProblemEntryEnum.BEGINNER ||
            args.name === NextProblemEntryEnum.EASY ||
            args.name === NextProblemEntryEnum.MEDIUM ||
            args.name === NextProblemEntryEnum.HARD;

        const isSpeedMode = args.name === NextProblemEntryEnum.SPEED;
        const isTraining = args.name === NextProblemEntryEnum.TRAINING;
        const levelProgressLevel = isSpeedMode
            ? args.levelFrom ?? args.rating
            : args.name === NextProblemEntryEnum.LEVEL_PROGRESS
              ? args.rating
              : null;

        const handleSkipSession = () => {
            return handleNext();
        };

        const [skipProblemTraining] = useSkipProblemTrainingMutation();
        const handleSkipForever = () => {
            void skipProblemTraining({problemId: problem.id})
                .unwrap()
                .then(() => handleNext())
                .catch(() => toast.error(t('single_problem.network_error_try_again')));
        };

        const handleSkipDuration = (duration: string) => {
            void skipProblemTraining({problemId: problem.id, duration})
                .unwrap()
                .then(() => handleNext())
                .catch(() => toast.error(t('single_problem.network_error_try_again')));
        };

        const formatTime = (milliseconds: number): string => {
            if (milliseconds <= 0) return '0.00';
            const totalCentiseconds = Math.ceil(milliseconds / 10);
            const seconds = Math.floor(totalCentiseconds / 100);
            const centiseconds = totalCentiseconds % 100;
            return `${seconds}:${centiseconds.toString().padStart(2, '0')}`;
        };

        const stopTimer = () => {
            setIsTimerRunning(false);
        };
        const resetTimer = () => {
            setTimeLeft(SPEED_SOLVE_SUBSEQUENT_MOVE_TIME_LIMIT);
            setIsTimerRunning(true);
        };
        const startTimer = () => {
            setTimeLeft(SPEED_SOLVE_FIRST_MOVE_TIME_LIMIT);
            setIsTimerRunning(true);
        };

        useEffect(() => {
            if (isSpeedMode) {
                startTimer();
            } else {
                setIsTimerRunning(false);
            }
        }, [problem.id, isSpeedMode]);

        const handleNextQueryParams = useProblemFilterQueryParams({
            lastProblemId: parseInt(problem.id.toString()!, 10),
            filterCursorElo: problem.elo,
        });

        const [fetchNextProblem, {isLoading: nextProblemIsLoading}] =
            useFetchNextProblemPostMutation();
        const [fetchLevelProgress] = levelsApi.useLazyGetLevelsQuery();
        const [startSrsSession] = useStartSrsSessionMutation();
        const [advanceSrsSession, {isLoading: advancingSrsSession}] =
            useAdvanceSrsSessionMutation();
        const [endSrsSession, {isLoading: endingSrsSession}] =
            useEndSrsSessionMutation();
        const {data: changesCount} = useFetchProblemChangesCountQuery(
            problem.id,
            {
                skip: !problem.id || !showDetails,
            }
        );

        const fetchLevelCompletion = async (level: number) => {
            const progress = await fetchLevelProgress(undefined, false).unwrap();
            const current = progress.find(entry => entry.level === level);
            const solvedCount = current
                ? isSpeedMode
                    ? current.speedSolvedCount
                    : current.solvedCount
                : 0;

            return {
                level,
                speed: isSpeedMode,
                complete: Boolean(
                    current &&
                        current.totalCount > 0 &&
                        solvedCount >= current.totalCount
                ),
                hasNextLevel: progress.some(
                    entry =>
                        entry.level !== null &&
                        entry.level > level &&
                        entry.level <= LAST_KNOWN_LEVEL &&
                        (isSpeedMode
                            ? entry.speedSolvedCount
                            : entry.solvedCount) < entry.totalCount
                ),
            };
        };

        const checkLevelCompletion = async (solvedProblemId: number) => {
            if (
                solvedProblemId !== problem.id ||
                !user ||
                isNil(levelProgressLevel) ||
                completedLevel ||
                checkingCompletion.current
            ) {
                return;
            }

            checkingCompletion.current = true;
            const context = completionContext.current;
            try {
                const completion = await fetchLevelCompletion(
                    levelProgressLevel
                );
                if (
                    !completion.complete ||
                    context !== completionContext.current
                ) {
                    return;
                }

                setCompletedLevel({
                    level: completion.level,
                    speed: completion.speed,
                    hasNextLevel: completion.hasNextLevel,
                });
            } catch {
                if (context === completionContext.current) {
                    toast.error(t('single_problem.network_error_try_again'));
                }
            } finally {
                checkingCompletion.current = false;
            }
        };

        const handleNextUnsolvedLevel = async () => {
            const noNextLevel = () => {
                setCompletedLevel(previous =>
                    previous ? {...previous, hasNextLevel: false} : previous
                );
                if (!completedLevel) {
                    toast.info(t('single_problem.no_problems_remain'));
                }
            };

            try {
                if (isSpeedMode) {
                    const currentLevel =
                        args.levelFrom ?? args.rating ?? problem.problemLevel;
                    let nextLevels = Array.from(
                        {length: Math.max(0, LAST_KNOWN_LEVEL - currentLevel)},
                        (_, index) => currentLevel + index + 1
                    );

                    if (user && nextLevels.length > 0) {
                        const progress = await fetchLevelProgress(
                            undefined,
                            false
                        ).unwrap();
                        const unfinishedLevels = new Set(
                            progress
                                .filter(
                                    level =>
                                        level.speedSolvedCount < level.totalCount
                                )
                                .map(level => level.level)
                        );
                        nextLevels = nextLevels.filter(level =>
                            unfinishedLevels.has(level)
                        );
                    }

                    for (const level of nextLevels) {
                        const {data: response, error} = await fetchNextProblem({
                            name: NextProblemEntryEnum.SPEED,
                            levelFrom: level,
                            levelTo: level + 1,
                            lastProblemId: problem.id,
                        });
                        if (error) {
                            if (
                                isFetchBaseQueryError(error) &&
                                error.status === 404
                            ) {
                                continue;
                            }
                            throw error;
                        }
                        if (response?.nextProblem) {
                            setCompletedLevel(null);
                            navigate(
                                buildNextProblemPath(
                                    response.nextProblem.id,
                                    response.argument
                                )
                            );
                            return;
                        }
                    }

                    noNextLevel();
                    return;
                }

                const response = await fetchNextProblem({
                    name: NextProblemEntryEnum.LEVEL_PROGRESS,
                    lastProblemId: problem.id,
                }).unwrap();

                if (!response.nextProblem) {
                    noNextLevel();
                    return;
                }

                setCompletedLevel(null);
                navigate(
                    buildNextProblemPath(
                        response.nextProblem.id,
                        response.argument
                    )
                );
            } catch (error) {
                if (isFetchBaseQueryError(error) && error.status === 404) {
                    noNextLevel();
                    return;
                }

                toast.error(t('single_problem.network_error_try_again'));
            }
        };

        const handleNext = async () => {
            if (completedLevel || checkingCompletion.current) return;

            if (isTraining) {
                try {
                    if (trainingProblemIds === null) {
                        const started = await startSrsSession().unwrap();
                        if (started === null) {
                            navigate('/training/cooldown');
                            return;
                        }
                    }
                    const session = await advanceSrsSession(problem.id).unwrap();
                    if (session === null) {
                        dispatch(uiSlice.actions.resetTrainingSession());
                        toast.info(t('training.srs.session_expired'));
                        navigate('/srs');
                        return;
                    }

                    const remainingIds = session.remainingProblemIds;
                    dispatch(uiSlice.actions.restoreTrainingSession(remainingIds));
                    const nextTrainingProblemId = remainingIds[0];
                    navigate(
                        nextTrainingProblemId === undefined
                            ? '/training/cooldown'
                            : buildNextProblemPath(nextTrainingProblemId, {
                                  name: NextProblemEntryEnum.TRAINING,
                              })
                    );
                } catch (error) {
                    console.error('Unable to advance the SRS session:', error);
                    toast.error(t('single_problem.network_error_try_again'));
                }
                return;
            }

            const {data: result, error} = await fetchNextProblem({
                ...handleNextQueryParams,
            });
            // Type guard to detect FetchBaseQueryError (has `status`)
            // If it's an RTK fetch error, ignore 404; otherwise treat as error
            if (
                error &&
                !(isFetchBaseQueryError(error) && error.status === 404)
            ) {
                toast.error(
                    <span>
                        {t('single_problem.network_error_try_again')}{' '}
                        <button
                            className="link"
                            onClick={() => handleNext()}
                            style={{
                                border: 'none',
                                background: 'none',
                                textDecoration: 'underline',
                                color: 'inherit',
                                cursor: 'pointer',
                                padding: 0,
                                font: 'inherit',
                            }}>
                            {t('single_problem.retry')}
                        </button>
                    </span>,
                    {
                        autoClose: 10000,
                        pauseOnHover: true,
                    }
                );
                return;
            } else if (
                error &&
                isFetchBaseQueryError(error) &&
                error.status === 404
            ) {
                console.info('No problems:', error);
            }

            if (result?.nextProblem && result.nextProblem.id !== problem.id) {
                navigate(
                    buildNextProblemPath(
                        result.nextProblem.id,
                        result.argument,
                        location.search,
                        {mergeCurrentSearch: true}
                    )
                );
            } else {
                if (!isNil(levelProgressLevel)) {
                    const level = levelProgressLevel;
                    let hasNextLevel = level < LAST_KNOWN_LEVEL;

                    if (user) {
                        if (checkingCompletion.current) return;
                        checkingCompletion.current = true;
                        const context = completionContext.current;
                        try {
                            const completion = await fetchLevelCompletion(level);
                            if (context !== completionContext.current) return;
                            hasNextLevel = completion.hasNextLevel;

                            if (completion.complete) {
                                setCompletedLevel({
                                    level,
                                    speed: isSpeedMode,
                                    hasNextLevel,
                                });
                                return;
                            }
                        } catch {
                            toast.error(
                                t('single_problem.network_error_try_again')
                            );
                            return;
                        } finally {
                            checkingCompletion.current = false;
                        }
                    }

                    toast.info(
                        <span>
                            {t(
                                'single_problem.no_problems_remain_in_the_level',
                                {
                                    level: getShortLevelName(level),
                                }
                            )}{' '}
                            {hasNextLevel && (
                                <button
                                    type="button"
                                    className="link"
                                    onClick={event => {
                                        event.stopPropagation();
                                        void handleNextUnsolvedLevel();
                                    }}
                                    style={{
                                        border: 'none',
                                        background: 'none',
                                        textDecoration: 'underline',
                                        color: 'inherit',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit',
                                    }}>
                                    {t(
                                        isSpeedMode
                                            ? 'single_problem.go_to_next_speed_level'
                                            : 'single_problem.go_to_next_unsolved_level'
                                    )}
                                </button>
                            )}
                        </span>,
                        {
                            autoClose: 20000,
                            pauseOnHover: true,
                        }
                    );
                    return;
                }

                toast.info(t('single_problem.no_problems_remain'));
            }
        };

        const handleEndSession = async () => {
            try {
                await endSrsSession().unwrap();
                dispatch(uiSlice.actions.resetTrainingSession());
                dispatch(uiSlice.actions.resetTrainingCounters());
                navigate('/srs');
            } catch {
                toast.error(t('single_problem.network_error_try_again'));
            }
        };

        const handleNextPotd = async () => {
            if (!dailyProblems || dailyProblems.length === 0) {
                toast.info(t('single_problem.no_problems_remain'));
                return;
            }

            const difficultyOrder = {
                beginner: 0,
                easy: 1,
                medium: 2,
                hard: 3,
            };

            const orderedDailyProblems = [...dailyProblems].sort(
                (left, right) =>
                    difficultyOrder[left.category] -
                    difficultyOrder[right.category]
            );

            const currentProblemIndex = orderedDailyProblems.findIndex(
                dailyProblem => dailyProblem.id === problem.id
            );

            const candidateDailyProblems =
                currentProblemIndex >= 0
                    ? [
                          ...orderedDailyProblems.slice(
                              currentProblemIndex + 1
                          ),
                          ...orderedDailyProblems.slice(0, currentProblemIndex),
                      ]
                    : orderedDailyProblems;

            const destinationDailyProblem = candidateDailyProblems.find(
                dailyProblem =>
                    dailyProblem.id !== problem.id &&
                    !dailyProblem.solvedByCurrentUser
            );

            if (!destinationDailyProblem) {
                toast.info(t('single_problem.no_problems_remain'));
                return;
            }

            navigate(
                `/problems/${destinationDailyProblem.id}/?name=${destinationDailyProblem.category}&dailyProblem=1`
            );
        };

        const showMobilePotdNextOptions =
            mobileLayout &&
            isDailyProblemContext &&
            isDifficultyMode &&
            !!dailyProblems?.length;

        const renderTooltip = (text: string) => <Tooltip>{text}</Tooltip>;

        const handleToggleDetails = () => setShowDetails(!showDetails);

        const boardSettingsButton = !mobileLayout && (
            <button
                type="button"
                onClick={() => showModal(MODAL_GLOBAL_BOARD_SETTINGS)}
                className="problem-settings-action"
                aria-label={t('board_settings.title')}
                title={t('board_settings.title')}>
                <FontAwesomeIcon
                    icon={faGear}
                    aria-hidden="true"
                    focusable="false"
                />
            </button>
        );

        const stars = problem.rating ? problem.rating.stars : 0;

        useEffect(() => {
            if (hideStars) return;

            if (!user) {
                setCanRate(false);
                setCanNotRateReason(
                    t(
                        'single_problem.you_must_be_logged_in_to_rate_on_a_problem'
                    )
                );
                return;
            }

            if (user.canRateProblems === false) {
                setCanRate(false);
                setCanNotRateReason(
                    t(
                        'single_problem.you_must_solve_at_least_20_problems_to_rate_on_a_problem'
                    )
                );
                return;
            }

            if (user.id === problem.author.id) {
                setCanRate(false);
                setCanNotRateReason(
                    t('single_problem.you_cannot_rate_on_your_own_problems')
                );
                return;
            }

            setCanRate(true);
            setCanNotRateReason(null);
        }, [user, problem, hideStars]);

        return (
            <div className="mt-2">
                <SendTrainingAchievementRequest />
                <ProblemFlagsModal problemId={problem.id} />
                <ProblemStarsModal problemId={problem.id} />
                <GlobalBoardSettingsModal />
                {completedLevel && (
                    <LevelCompletedModal
                        {...completedLevel}
                        onNext={handleNextUnsolvedLevel}
                        onHide={() => setCompletedLevel(null)}
                    />
                )}
                {canChangeLevel && (
                    <ChangeLevelModal
                        problemId={problem.id}
                        currentLevel={getShortLevelName(
                            problem.rank.value *
                                (problem.rank.unit === 'dan' ? 1 : -1) +
                                (problem.rank.unit === 'dan' ? 29 : 30)
                        )}
                    />
                )}

                <div className="flex pl-1 sm:pl-0">
                    <div className="col-lg-auto flex min-w-0 flex-1 flex-wrap gap-1.5 sm:gap-1">
                        <Button
                            variant="secondary"
                            className="btn btn-secondary !w-[38px] !rounded-sm !px-0"
                            onClick={handleToggleDetails}
                            aria-label={t('navbar.see_details')}
                            aria-expanded={showDetails}
                            title={t('navbar.see_details')}>
                            <FontAwesomeIcon
                                aria-hidden="true"
                                icon={faChevronDown}
                                className="origin-center transition-transform duration-200 ease-in-out motion-reduce:transition-none"
                                style={{
                                    transform: showDetails
                                        ? 'rotate(180deg)'
                                        : 'rotate(0deg)',
                                }}
                            />
                        </Button>
                        {!hideNext && (
                            <OverlayTrigger
                                placement="top"
                                delay={{show: 250, hide: 250}}
                                overlay={
                                    !mobileLayout ? (
                                        <Tooltip id={'tooltip_btn'}>
                                            {t('common.next_shortcut')}
                                        </Tooltip>
                                    ) : (
                                        <></>
                                    )
                                }>
                                <span className="d-inline-block shrink-0">
                                    {showMobilePotdNextOptions ? (
                                        <MobilePotdNextButton
                                            disabled={
                                                nextProblemIsLoading ||
                                                advancingSrsSession
                                            }
                                            difficultyActionLabel={`${t('single_problem.next')} ${upperFirst(t(`single_problem.next_types.${args.name}`))}`}
                                            potdActionLabel={`${t('single_problem.next')} POTD`}
                                            onDifficultyClick={handleNext}
                                            onPotdClick={handleNextPotd}
                                        />
                                    ) : (
                                        <NextBtn
                                            problem={problem}
                                            disabled={
                                                nextProblemIsLoading ||
                                                advancingSrsSession
                                            }
                                            onClick={handleNext}
                                        />
                                    )}
                                </span>
                            </OverlayTrigger>
                        )}
                        {isTraining && (
                            <>
                                <SkipButton
                                    onSkipSession={handleSkipSession}
                                    onSkipForever={handleSkipForever}
                                    onSkipDuration={handleSkipDuration}
                                />
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    disabled={endingSrsSession}
                                    onClick={() => void handleEndSession()}>
                                    {t('training.srs.end_session')}
                                </Button>
                            </>
                        )}

                        {isSpeedMode && (
                            <span
                                className={`flex items-center font-mono text-sm font-semibold sm:ml-2 ${
                                    theme === 'dark'
                                        ? 'text-neutral-300'
                                        : 'text-neutral-700'
                                }`}>
                                {formatTime(timeLeft)}
                            </span>
                        )}

                        <OverlayTrigger
                            placement="top"
                            overlay={renderTooltip('Problem rating')}>
                            <span className="btn btn-outline pe-none shrink-0 border-0 px-0 font-bold sm:px-1">
                                <RankText rank={problem.rank} />
                            </span>
                        </OverlayTrigger>

                        {problem.specificGenre &&
                            problem.specificGenre !==
                                ProblemGenreEnum.LIFE_AND_DEATH &&
                            !hideGenre && (
                                <span className="btn btn-outline pe-none hidden shrink-0 border-0 px-0 sm:inline-flex sm:px-1">
                                    {problem.specificGenre}
                                </span>
                            )}

                        {effectiveAttempts && (
                            <StatusIcon
                                hideCheckMarks={hideCheckMarks}
                                effectiveAttempts={effectiveAttempts}
                            />
                        )}

                        {problem.flagStats.flagSum < 0 && !hideFlags && (
                            <OverlayTrigger
                                placement="top"
                                overlay={renderTooltip('Has bad flags')}>
                                <span className="btn btn-outline-danger btn pe-none shrink-0 border-0 px-0 sm:px-1">
                                    <i className="fa-solid fa-flag" />
                                </span>
                            </OverlayTrigger>
                        )}

                        <StarRating
                            problemId={problem.id}
                            canRate={canRate}
                            ratedFor={problem.rating?.currentUserVote || 0}
                            canNotRateReason={canNotRateReason}
                            stars={stars}
                        />

                        {dailyProblems &&
                            dailyProblems.length > 0 &&
                            (args.name === NextProblemEntryEnum.BEGINNER ||
                                args.name === NextProblemEntryEnum.EASY ||
                                args.name === NextProblemEntryEnum.MEDIUM ||
                                args.name === NextProblemEntryEnum.HARD) && (
                                <DailyProblemsButtons
                                    dailyProblems={dailyProblems}
                                    compactLayout={mobileLayout}
                                />
                            )}
                    </div>
                </div>

                <AnimatedCollapse expanded={showDetails}>
                    <div className="flex mt-2">
                        <div className="flex-1">
                            <Card>
                                <Card.Body className="flex flex-col !p-0">
                                    <div className="flex flex-wrap items-center justify-end gap-2 border-b border-black/10 px-3 py-2.5 sm:px-4 sm:py-3 dark:border-[var(--ui-border)]">
                                        {user ? (
                                            <ProblemCollection
                                                key={problem.id}
                                                problemId={problem.id}
                                                collections={
                                                    problem.collections
                                                }
                                                isFavourite={
                                                    problem.isFavorited
                                                }
                                                userId={user.id}
                                                isEditable={problem.canBeEdited}
                                                openRef={collectionModalRef}
                                                beforeAddTo={boardSettingsButton}
                                            />
                                        ) : (
                                            boardSettingsButton
                                        )}
                                        {!mobileLayout && (
                                            <Button
                                                href={`/problems/${problem.id}/research`}
                                                variant="secondary"
                                                size="sm">
                                                {t('single_problem.research')}
                                            </Button>
                                        )}
                                        {!mobileLayout && (
                                            <FlagButton problem={problem} />
                                        )}
                                        {canChangeLevel && !mobileLayout && (
                                            <Button
                                                onClick={() =>
                                                    showModal(
                                                        MODAL_CHANGE_LEVEL
                                                    )
                                                }
                                                variant="secondary"
                                                size="sm">
                                                {t(
                                                    'single_problem.change_level'
                                                )}
                                            </Button>
                                        )}
                                        {mobileLayout && (
                                            <Dropdown className="shrink-0">
                                                <Dropdown.Toggle
                                                    variant="secondary"
                                                    size="sm"
                                                    className="!h-[31px] !w-[31px] !rounded !px-0 [&>span:last-child]:hidden"
                                                    aria-label={t('common.more_actions')}
                                                    title={t('common.more_actions')}>
                                                    <FontAwesomeIcon
                                                        aria-hidden="true"
                                                        icon={faEllipsis}
                                                    />
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu align="end">
                                                    <Dropdown.Item
                                                        onClick={() =>
                                                            showModal(
                                                                MODAL_GLOBAL_BOARD_SETTINGS
                                                            )
                                                        }>
                                                        <FontAwesomeIcon
                                                            aria-hidden="true"
                                                            className="mr-2 w-4 text-neutral-500"
                                                            icon={faGear}
                                                        />
                                                        {t(
                                                            'board_settings.title'
                                                        )}
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        href={`/problems/${problem.id}/research`}>
                                                        <FontAwesomeIcon
                                                            aria-hidden="true"
                                                            className="mr-2 w-4 text-neutral-500"
                                                            icon={faCalculator}
                                                        />
                                                        {t(
                                                            'single_problem.research'
                                                        )}
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        onClick={() =>
                                                            showModal(
                                                                MODAL_PROBLEM_FLAGS
                                                            )
                                                        }>
                                                        <FontAwesomeIcon
                                                            aria-hidden="true"
                                                            className="mr-2 w-4 text-neutral-500"
                                                            icon={faFlagSolid}
                                                        />
                                                        {t(
                                                            'single_problem.add_view_flags'
                                                        )}
                                                        {problem.flagStats
                                                            .positiveFlagSum >
                                                            0 && (
                                                            <span className="ml-1">
                                                                +
                                                                {
                                                                    problem
                                                                        .flagStats
                                                                        .positiveFlagSum
                                                                }
                                                            </span>
                                                        )}
                                                        {problem.flagStats
                                                            .negativeFlagSum <
                                                            0 && (
                                                            <span className="ml-1">
                                                                {
                                                                    problem
                                                                        .flagStats
                                                                        .negativeFlagSum
                                                                }
                                                            </span>
                                                        )}
                                                    </Dropdown.Item>
                                                    {canChangeLevel && (
                                                        <Dropdown.Item
                                                            onClick={() =>
                                                                showModal(
                                                                    MODAL_CHANGE_LEVEL
                                                                )
                                                            }>
                                                            <FontAwesomeIcon
                                                                aria-hidden="true"
                                                                className="mr-2 w-4 text-neutral-500"
                                                                icon={faSliders}
                                                            />
                                                            {t(
                                                                'single_problem.change_level'
                                                            )}
                                                        </Dropdown.Item>
                                                    )}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        )}
                                        {canEditProblem && (
                                            <Button
                                                href={`/problems/${problem.id}/edit`}
                                                variant="secondary"
                                                size="sm">
                                                {t('common.edit')}
                                            </Button>
                                        )}
                                        {user && (
                                            <Button
                                                href={suggestionHref}
                                                variant="secondary"
                                                size="sm">
                                                {`Suggest Edit ${problem.suggestionsOpenCount > 0 ? `(${problem.suggestionsOpenCount})` : ''}`}
                                            </Button>
                                        )}
                                    </div>
                                    <dl className="grid grid-cols-1 lg:grid-cols-2">
                                        <ProblemDetailItem
                                            label={t('common.author')}>
                                            <User user={problem.author} />
                                        </ProblemDetailItem>

                                        <ProblemDetailItem
                                            label={t(
                                                'single_problem.created'
                                            )}>
                                            <span className="whitespace-nowrap">
                                                {dayjs(
                                                    problem.createdAt
                                                ).format('YYYY-MM-DD')}
                                            </span>
                                            {!isNil(changesCount) &&
                                                changesCount > 0 && (
                                                    <span className="inline-flex whitespace-nowrap">
                                                        <span
                                                            className="mx-1 text-neutral-400"
                                                            aria-hidden="true">
                                                            ·
                                                        </span>
                                                        <Link
                                                            className="link lowercase"
                                                            to={
                                                                '/problems/' +
                                                                problem.id +
                                                                '/edit-history'
                                                            }>
                                                            {t(
                                                                'single_problem.history'
                                                            )}{' '}
                                                            ({changesCount})
                                                        </Link>
                                                    </span>
                                                )}
                                        </ProblemDetailItem>

                                        <ProblemDetailItem
                                            label={t(
                                                'single_problem.difficulty'
                                            )}>
                                            <RankText rank={problem.rank} />
                                            <span className="inline-flex whitespace-nowrap">
                                                <span
                                                    className="mx-1 text-neutral-400"
                                                    aria-hidden="true">
                                                    ·
                                                </span>
                                                <Link
                                                    className="link lowercase"
                                                    to={
                                                        '/problems/' +
                                                        problem.id +
                                                        '/rating-history'
                                                    }>
                                                    {t(
                                                        'single_problem.history'
                                                    )}
                                                </Link>
                                            </span>
                                        </ProblemDetailItem>

                                        <ProblemDetailItem
                                            label={t(
                                                'single_problem.source'
                                            )}>
                                            {problem.source || '-'}
                                        </ProblemDetailItem>

                                        <ProblemDetailItem
                                            label={t(
                                                'single_problem.avg_solve_time'
                                            )}>
                                            {problem.avgSolveTimeSeconds > 0 ? (
                                                <>
                                                {problem.avgSolveTimeSeconds}{' '}
                                                <span className="lowercase">
                                                    {t('common.seconds_short')}
                                                </span>
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </ProblemDetailItem>

                                        {!hideStars && (
                                            <ProblemDetailItem
                                                label={t(
                                                    'single_problem.stars'
                                                )}>
                                                {problem.rating &&
                                                    problem.rating.votes > 0 && (
                                                        <StarContent
                                                            starRating={
                                                                problem.rating
                                                            }
                                                        />
                                                    )}
                                            </ProblemDetailItem>
                                        )}

                                        <ProblemDetailItem
                                            label={t(
                                                'single_problem.user_attempts'
                                            )}>
                                            {problem.attempts.tries > 0 ? (
                                                <UserAttemptsContent
                                                    problemId={problem.id}
                                                    userAttempts={
                                                        problem.attempts
                                                    }
                                                />
                                            ) : (
                                                '-'
                                            )}
                                        </ProblemDetailItem>

                                        <ProblemDetailItem
                                            label={t(
                                                'single_problem.collections'
                                            )}>
                                            <div className="flex min-w-0 flex-col items-start gap-1.5">
                                                {problem.collections.length >
                                                    0 ||
                                                problem.isFavorited ? (
                                                    <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0">
                                                        <CollectionList
                                                            collections={
                                                                problem.collections
                                                            }
                                                            isFavorited={
                                                                problem.isFavorited
                                                            }
                                                        />
                                                    </ul>
                                                ) : (
                                                    !user && <span>-</span>
                                                )}
                                                {user && (
                                                    <button
                                                        type="button"
                                                        className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-xs text-neutral-500 transition-colors hover:text-neutral-800 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:text-neutral-400 dark:hover:text-neutral-200"
                                                        onClick={() =>
                                                            collectionModalRef.current?.open()
                                                        }
                                                        aria-label={
                                                            problem.canBeEdited
                                                                ? t('problem_collections.add_to.add')
                                                                : t('problem_collections.add_to.suggest_collection')
                                                        }
                                                        title={
                                                            problem.canBeEdited
                                                                ? t('problem_collections.add_to.add')
                                                                : t('problem_collections.add_to.suggest_collection')
                                                        }>
                                                        <span
                                                            aria-hidden="true"
                                                            className="text-sm font-normal leading-none">
                                                            +
                                                        </span>
                                                        <span>
                                                            {problem.canBeEdited
                                                                ? t('problem_collections.add_to.add')
                                                                : t('problem_collections.add_to.suggest_collection')}
                                                        </span>
                                                    </button>
                                                )}
                                            </div>
                                        </ProblemDetailItem>

                                        <ProblemDetailItem
                                            label={t('single_problem.genre')}>
                                            {problem.specificGenre &&
                                            problem.specificGenre !==
                                                problem.genre
                                                ? problem.specificGenre
                                                : problem.genre}{' '}
                                            {problem.isCanon && (
                                                <span title={t('problems_filter.canon')}>ⓒ</span>
                                            )}
                                            {problem.isStandard &&
                                                !problem.isCanon && (
                                                    <span title={t('problems_filter.standard')}>
                                                        ⓢ
                                                    </span>
                                                )}
                                        </ProblemDetailItem>

                                        <ProblemDetailItem
                                            label={t(
                                                'single_problem.your_attempts'
                                            )}>
                                            {problem.currentUserAttempts &&
                                            problem.currentUserAttempts.tries >
                                                0 ? (
                                                    <Link
                                                        className="link"
                                                        to={
                                                            '/problems/' +
                                                            problem.id +
                                                            '/paths-history?tab=my-attempts'
                                                        }>
                                                        {
                                                            problem
                                                                .currentUserAttempts
                                                                .solved
                                                        }{' '}
                                                        /{' '}
                                                        {
                                                            problem
                                                                .currentUserAttempts
                                                                .tries
                                                        }
                                                    </Link>
                                                ) : (
                                                    '-'
                                                )}
                                        </ProblemDetailItem>

                                        <ProblemDetailItem
                                            className="lg:col-span-2"
                                            label={t(
                                                'single_problem.similar_problems'
                                            )}>
                                            <PotentialDuplicates
                                                pattern={problem.sgf}
                                                placement={
                                                    mobileLayout ? 'bottom' : 'right'
                                                }
                                                showPercentageInButton={false}
                                                showR1Count={false}
                                                excludedIds={[problem.id]}
                                                showInfo={false}
                                                compactButtonLabel={mobileLayout}
                                            />
                                        </ProblemDetailItem>
                                    </dl>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </AnimatedCollapse>
            </div>
        );
    }
);

ProblemHeader.displayName = 'ProblemHeader';
